import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { api } from "../config/api";

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};

// Fallback list (used only if the /api/cities endpoint is unreachable).
// The /api/cities endpoint is the source of truth. The hardcoded list is
// removed from this file — admin manages cities via the admin panel.
const FALLBACK_CITIES = [
  {
    id: "kochi",
    name: "Kochi",
    state: "Kerala",
    lat: 9.9312,
    lng: 76.2673,
    available: true,
  },
  {
    id: "kollam",
    name: "Kollam",
    state: "Kerala",
    lat: 8.8932,
    lng: 76.6141,
    available: true,
  },
  {
    id: "karunagapally",
    name: "Karunagapally",
    state: "Kerala",
    lat: 9.049435,
    lng: 76.535519,
    available: true,
  },
  {
    id: "kariyad",
    name: "Kariyad",
    state: "Kerala",
    lat: 10.161559,
    lng: 76.364807,
    available: true,
  },
  {
    id: "angamaly",
    name: "Angamaly",
    state: "Kerala",
    lat: 10.19,
    lng: 76.39,
    available: true,
  },
  {
    id: "thalayolaparambu",
    name: "Thalayolaparambu",
    state: "Kerala",
    lat: 9.785552,
    lng: 76.452892,
    available: true,
  },
];

// Selected city is essential for showtimes — always persisted, independent of preference cookies
const loadSelectedCity = (cities) => {
  try {
    const saved = localStorage.getItem("mycinemas_selectedCity");
    if (saved) {
      const city = JSON.parse(saved);
      // If it's a custom location, return it as-is
      if (city.isCustom) {
        return city;
      }
      // Prefer canonical entry from current cities list when available
      const foundCity = cities.find((c) => c.id === city.id);
      if (foundCity) {
        return foundCity;
      }
      // API/geocoded cities may not be in the list — keep saved selection
      if (city.name && city.lat != null && city.lng != null) {
        return city;
      }
    }
  } catch (error) {
    console.error("Error loading selected city from localStorage:", error);
  }
  return null;
};

const saveSelectedCity = (city) => {
  try {
    if (city) {
      localStorage.setItem("mycinemas_selectedCity", JSON.stringify(city));
    } else {
      localStorage.removeItem("mycinemas_selectedCity");
    }
  } catch (error) {
    console.error("Error saving selected city to localStorage:", error);
  }
};

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Most specific place name from reverse geocode — no snapping to cities list
const getExactLocationName = (geo) => {
  const admin = geo.localityInfo?.administrative || [];
  const mostSpecificAdmin = [...admin].sort(
    (a, b) => b.adminLevel - a.adminLevel,
  )[0];

  return geo.locality || mostSpecificAdmin?.name || geo.city || null;
};

const buildExactLocation = (geo, lat, lng, allCities) => {
  const name = getExactLocationName(geo);
  if (!name) return null;

  // Logic added to check with db cities before dispaying the name
  // If the distance of the location provided from bigdata api > 10 it will disaplay the name from bigdata api
  // if the distance <=10 it will cross check with database and show a nearby location for more accuracy
  // This should also fix the issue of showing Angamali instead of Angamaly as Angamali is returned from BigData API
  // See: https://api-bdc.io/data/reverse-geocode-client?latitude=10.1953035&longitude=76.3838005&localityLanguage=en
  
  const MAX_MATCH_DISTANCE = 10; // km
  // Find nearest city from our database
  let matchedCity = null;

  if (allCities && allCities.length > 0) {
    const nearest = [...allCities]
      .map(city => ({
        ...city,
        distance: calculateDistance(lat, lng, city.lat, city.lng),
      }))
      .sort((a, b) => a.distance - b.distance)[0];

    if (nearest && nearest.distance <= MAX_MATCH_DISTANCE) {
      matchedCity = nearest;
    }
  }

  // Use DB name if available
  const displayName = matchedCity ? matchedCity.name : name;  

  const state = geo.principalSubdivision || "";
  // const formattedAddress = [name, state, geo.countryName]
  const formattedAddress = [displayName, state, geo.countryName]
    .filter(Boolean)
    .join(", ");

  return {
    // id: `geocoded-${name.toLowerCase().replace(/\s+/g, "-")}-${Math.round(lat * 10000)}-${Math.round(lng * 10000)}`,
    // id: `geocoded-${displayName.toLowerCase().replace(/\s+/g, "-")}-${Math.round(lat * 10000)}-${Math.round(lng * 10000)}`,
    id: matchedCity
      ? matchedCity.id
      : `geocoded-${displayName.toLowerCase().replace(/\s+/g, "-")}-${Math.round(lat * 10000)}-${Math.round(lng * 10000)}`,
    // name,
    name: displayName,
    state,
    lat,
    lng,
    available: true,
    // isCustom: true,
    isCustom: !matchedCity,
    isGeocoded: true,
    formattedAddress,
  };
};

export const LocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(null);

  // Cities list — fetched from /api/cities (the source of truth in the DB).
  // Starts as empty array; populated on mount.
  const [allCities, setAllCities] = useState([]);

  // Load selected city from localStorage, defaulting to the first available city
  // (or fallback list while /api/cities is loading).
  const [selectedCity, setSelectedCity] = useState(() => {
    // Initial: try localStorage, but we don't have the cities list yet.
    // The useEffect below will re-resolve the saved city against the loaded list.
    try {
      const saved = localStorage.getItem("mycinemas_selectedCity");
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {
      /* ignore */
    }
    return null;
  });

  const [nearbyCities, setNearbyCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isLocationRequestInProgress = useRef(false);
  const hasAttemptedLocation = useRef(false);

  // =========================================================================
  // Fetch cities from /api/cities on mount
  // =========================================================================
  useEffect(() => {
    let isMounted = true;

    const loadCities = async () => {
      try {
        const data = await api.getCities();
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        if (list.length > 0) {
          setAllCities(list);
          // Re-resolve selectedCity against the fresh list.
          // If the saved city exists in the new list, use the canonical entry.
          // Otherwise keep the saved city (geocoded/custom).
          setSelectedCity((prev) => {
            if (!prev) {
              return list[0]; // Default to first city (Kollam — has cinemas)
            }
            if (prev.isCustom) {
              return prev; // Geocoded/custom — keep as-is
            }
            const found = list.find((c) => c.id === prev.id);
            if (found) return found;
            return prev; // Saved city not in list — keep it
          });
        } else {
          // API returned empty — use fallback
          setAllCities(FALLBACK_CITIES);
        }
      } catch (err) {
        if (!isMounted) return;
        console.warn("Failed to load cities from API, using fallback", err);
        setAllCities(FALLBACK_CITIES);
      }
    };

    loadCities();

    return () => {
      isMounted = false;
    };
  }, []);

  // Re-hydrate from storage when consent banner updates (e.g. first accept)
  useEffect(() => {
    const handleConsentUpdate = () => {
      const savedCity = loadSelectedCity(allCities);
      if (savedCity) {
        setSelectedCity(savedCity);
      }
    };

    window.addEventListener("cookieConsentUpdated", handleConsentUpdate);
    return () => {
      window.removeEventListener("cookieConsentUpdated", handleConsentUpdate);
    };
  }, [allCities]);

  // Find nearby cities based on user location (memoized)
  // autoSelectNearest: only true when GPS/geocode failed and we need a fallback city
  const findNearbyCities = useCallback(
    (userLat, userLng, autoSelectNearest = false) => {
      // Use the loaded cities list (from API). Fallback to FALLBACK_CITIES if empty.
      const source = allCities.length > 0 ? allCities : FALLBACK_CITIES;
      const citiesWithDistance = source
        .filter((city) => city.available !== false)
        .map((city) => ({
          ...city,
          distance: calculateDistance(userLat, userLng, city.lat, city.lng),
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 5);

      setNearbyCities(citiesWithDistance);

      if (autoSelectNearest && citiesWithDistance.length > 0) {
        const nearestCity = citiesWithDistance[0];
        setSelectedCity(nearestCity);
        saveSelectedCity(nearestCity);
      }
    },
    [allCities],
  );

  const getUserLocation = useCallback(
    (forceRetry = false) => {
      if (forceRetry) {
        isLocationRequestInProgress.current = false;
      }

      if (isLocationRequestInProgress.current) return;

      if (!navigator.geolocation) {
        setError("Geolocation is not supported by this browser.");
        return;
      }

      hasAttemptedLocation.current = true;
      isLocationRequestInProgress.current = true;
      setIsLoading(true);
      setError(null);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });

          // Reverse geocode to get actual city name
          try {
            const res = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            );
            const geo = await res.json();
            const exactLocation = buildExactLocation(geo, latitude, longitude, allCities);

            if (exactLocation) {
              setSelectedCity(exactLocation);
              saveSelectedCity(exactLocation);
            } else {
              findNearbyCities(latitude, longitude, true);
            }
          } catch (geoErr) {
            console.warn(
              "Reverse geocode failed, using nearest city fallback",
              geoErr,
            );
            findNearbyCities(latitude, longitude, true);
          }

          // Update nearby list only — do not overwrite API-detected selected city
          findNearbyCities(latitude, longitude, false);

          setIsLoading(false);
          isLocationRequestInProgress.current = false;
          setError(null);
        },
        (error) => {
          console.error("Error getting location:", error);
          isLocationRequestInProgress.current = false;
          setIsLoading(false);

          let errorMessage =
            "Unable to get your location. Please select a city manually.";

          if (error.code === 1) {
            errorMessage =
              "Location permission denied. Please allow location access and try again.";
          } else if (error.code === 2) {
            errorMessage =
              "Location unavailable. Please select a city manually.";
          } else if (error.code === 3) {
            errorMessage = "Location request timed out. Please try again.";
          }

          if (forceRetry) {
            setError(errorMessage);
          } else {
            setError(null);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        },
      );
    },
    [findNearbyCities, allCities],
  );

  // Select a city manually
  const selectCity = (city) => {
    if (!city) {
      setSelectedCity(null);
      saveSelectedCity(null);
      return;
    }
    setSelectedCity(city);
    saveSelectedCity(city);
    // Sync userLocation to the city's coordinates so downstream distance
    // calculations (MovieArea now-showing lookup, NearbyLocationsList
    // suggestions, CinemaListingPage geo fetch) use the selected city
    // center instead of stale GPS. Without this, picking Angamaly on
    // an Anekal-locked device would still rank cinemas by distance from
    // Anekal (hundreds of km away) and miss every Angamaly cinema.
    if (Number.isFinite(city.lat) && Number.isFinite(city.lng)) {
      setUserLocation({ lat: city.lat, lng: city.lng });
    }
  };

  // Select a custom location (not in the predefined list)
  const selectCustomLocation = (locationName) => {
    const fallback = allCities[0] || FALLBACK_CITIES[0];
    const customCity = {
      id: `custom-${locationName.toLowerCase().replace(/\s+/g, "-")}`,
      name: locationName,
      state: "",
      lat: selectedCity?.lat || fallback.lat,
      lng: selectedCity?.lng || fallback.lng,
      available: true,
      isCustom: true,
    };
    setSelectedCity(customCity);
    saveSelectedCity(customCity);
    setError(null);
  };

  // Select a geocoded location from search / places API
  const selectGeocodedLocation = (locationData) => {
    const { name, state, lat, lng, formattedAddress } = locationData;

    const geocodedCity = {
      id: `geocoded-${name.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`,
      name: name,
      state: state || "",
      lat: lat,
      lng: lng,
      available: true,
      isCustom: true,
      isGeocoded: true,
      formattedAddress: formattedAddress || name,
    };

    setSelectedCity(geocodedCity);
    saveSelectedCity(geocodedCity);
    setError(null);

    setUserLocation({ lat: lat, lng: lng });
    findNearbyCities(lat, lng, false);
  };

  // Get location coordinates for API calls (prioritize GPS, fallback to selected city)
  const getLocationForAPI = () => {
    if (userLocation && userLocation.lat && userLocation.lng) {
      return {
        lat: userLocation.lat,
        lng: userLocation.lng,
        source: "gps",
      };
    }
    if (selectedCity && selectedCity.lat && selectedCity.lng) {
      return {
        lat: selectedCity.lat,
        lng: selectedCity.lng,
        source: "city",
      };
    }
    return null;
  };

  // Format distance for display
  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m away`;
    }
    return `${distance.toFixed(1)}km away`;
  };

  const value = {
    userLocation,
    selectedCity,
    nearbyCities,
    allCities,
    isLoading,
    error,
    getUserLocation,
    selectCity,
    selectCustomLocation,
    selectGeocodedLocation,
    formatDistance,
    findNearbyCities,
    getLocationForAPI,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
